import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

// Post-tournament nav: once the competition wrapped, the navbar surfaces only
// "2026" (every day's matches — the archived results) and the "Konzultáció"
// article. The other pages (Meccsek, Pontok, Kontaktok, Szabályok, Időjárás,
// Képek) stay reachable by URL — uncomment to bring them back into the bar.
// (/kerdoiv, /ranglista and /admin remain intentionally URL-only; /parositasok
// and /statisztika are not mapped.)
const navItems: NavItem[] = [
  { to: '/konzultacio', label: 'Konzultáció' },
  { to: '/napok', label: '2026' },
  // URL-only for now — uncomment to surface in the navbar:
  // { to: '/kerdoiv', label: 'Kérdőív' },
  // { to: '/meccsek', label: 'Meccsek' },
  // { to: '/pontok', label: 'Pontok' },
  // { to: '/kontaktok', label: 'Kontaktok' },
  // { to: '/szabalyok', label: 'Szabályok' },
  // { to: '/ido', label: 'Időjárás' },
  // { to: '/kepek', label: 'Képek' },
];

/**
 * AppShell — ported from MainLayout.razor + NavMenu.razor.
 * Sidebar uses a green-deep -> green gradient (was violet-900 -> blue-700),
 * white text, "Umagi Torna 26'" title, responsive hamburger collapse on mobile,
 * tennis-ball marker on the active link.
 */
export default function AppShell() {
  // Mirrors NavMenu._collapseNavMenu (collapsed = menu hidden on mobile).
  const [collapsed, setCollapsed] = useState(true);

  const toggle = () => setCollapsed((c) => !c);

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      {/* sidebar */}
      <aside
        className={clsx(
          'fixed top-0 right-0 left-0 z-20 w-full shrink-0 bg-gradient-to-b from-green-deep to-green md:sticky md:left-auto md:right-auto md:h-screen md:w-64',
          collapsed ? 'h-14' : 'h-auto',
        )}
      >
        <div className="bg-gradient-to-br from-green-deep to-green">
          <div className="nav-header flex h-14 items-center px-3 text-white">
            <div className="flex w-full items-center justify-between">
              <NavLink
                to="/"
                className="flex items-center gap-2.5 font-display text-lg font-semibold"
              >
                <img
                  src="/umagitorna.svg"
                  alt=""
                  className="h-9 w-9 shrink-0 drop-shadow-sm"
                />
                <span>Umagi Torna 26&apos;</span>
              </NavLink>
              <button
                type="button"
                aria-controls="mobile-menu"
                aria-expanded={!collapsed}
                onClick={toggle}
                className="relative inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-white/10 hover:text-gray-200 focus:ring-2 focus:ring-inset focus:ring-white focus:outline-hidden md:hidden"
              >
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open main menu</span>
                {/* Icon when menu is closed */}
                <svg
                  className={clsx(collapsed ? 'block' : 'hidden', 'size-6')}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
                {/* Icon when menu is open */}
                <svg
                  className={clsx(collapsed ? 'hidden' : 'block', 'size-6')}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* White separator: on mobile only when menu open, always on md */}
          {!collapsed && (
            <div className="mx-auto h-px w-[90%] bg-white md:hidden" />
          )}
          <div className="mx-auto hidden h-px w-[90%] bg-white md:block" />

          <div
            className={clsx(
              collapsed ? 'hidden' : 'block',
              'text-white md:block md:h-[calc(100vh-3.5rem)] md:overflow-y-auto',
            )}
          >
            <div className="mx-auto w-[90%]">
              <nav className="flex flex-col divide-y divide-white/20">
                {navItems.map((item) => (
                  <div key={item.to} className="px-3 py-2 text-base">
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={() => setCollapsed(true)}
                      className={({ isActive }) =>
                        clsx(
                          'group flex w-full items-center px-3 py-3 text-lg font-medium',
                          isActive && 'font-bold',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span>{item.label}</span>
                          <img
                            src="/tennis_ball.png"
                            alt=""
                            className={clsx(
                              'marker-img ml-auto h-7 w-7 object-contain transition-opacity duration-200 group-hover:opacity-100',
                              isActive ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </>
                      )}
                    </NavLink>
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </aside>

      {/* main content */}
      <main
        className={clsx(
          'flex-1',
          collapsed ? 'mt-14 md:mt-0' : 'mt-auto md:mt-0',
        )}
      >
        <article>
          <Outlet />
        </article>
      </main>
    </div>
  );
}
